import { isEqualWith } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { useCreateTemplateVariations, useModal } from 'src/hooks';
import { IDyTemplate } from 'src/hooks/api/product/dyTemplates';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { IProduct } from 'src/interfaces';
import { lang } from 'src/utils';

import { VariationMap } from '../../addVariationTemplatePage/modules/variationTemplateMappingStep/VariationTemplateMappingStep.utils';

interface UseEditVariationTemplatePageProps {
  productCode?: string;
  variationCode?: string;
  selectedTemplate?: IDyTemplate;
  product?: IProduct;
  isProductLoading: boolean;
  shouldRemoveItemRef: React.MutableRefObject<boolean>;
  variationMap: VariationMap;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UseEditVariationTemplatePageReturn {
  discardModal: ReturnType<typeof useModal>;
  handleDiscard: () => void;
  handleDiscardConfirm: () => void;
  handleVariationTemplateNext: () => Promise<void>;
  initialVariationMap: VariationMap;
  isVariationMapDirty: boolean;
}

export const useEditVariationTemplatePageHandlers = ({
  productCode = '',
  variationCode = '',
  selectedTemplate,
  product,
  isProductLoading,
  shouldRemoveItemRef,
  variationMap,
  isSubmitting,
  setIsSubmitting,
}: UseEditVariationTemplatePageProps): UseEditVariationTemplatePageReturn => {
  const navigate = useNavigate();
  const createTemplateVariations = useCreateTemplateVariations();
  const discardModal = useModal();
  const skipBlockRef = useRef(false);

  const initialVariationMap = useMemo<VariationMap>(() => {
    if (!selectedTemplate || isProductLoading) return {};

    const matchedVariation = product?.productVariations?.find(
      (v) => v.product_code === variationCode,
    );
    const templateVariations = matchedVariation?.variation_template?.variations;

    return Object.fromEntries(
      Array.from({
        length: selectedTemplate.number_of_variations || 0,
      }).map((_, index) => [
        index + 1,
        {
          productCode: index === 0 ? productCode : '',
          variationCode:
            index === 0
              ? variationCode
              : templateVariations?.find((v) => v.order === index + 1)
                  ?.product_code || '',
          showFlavors: templateVariations?.find((v) => v.order === index + 1)
            ?.config?.showFlavors,
        },
      ]),
    );
  }, [selectedTemplate, isProductLoading, product, variationCode, productCode]);

  const isVariationMapDirty = useMemo(
    () =>
      !isEqualWith(variationMap, initialVariationMap, (val1, val2, key) => {
        if (key === undefined) return undefined;

        return (
          val1?.variationCode === val2?.variationCode &&
          val1?.showFlavors === val2?.showFlavors
        );
      }),
    [variationMap, initialVariationMap],
  );

  const shouldBlockNavigation = useCallback(
    () => !skipBlockRef.current && isVariationMapDirty && !isSubmitting,
    [isVariationMapDirty, isSubmitting],
  );

  useNavigateBlocker(shouldBlockNavigation);

  const skipNextBlock = () => {
    skipBlockRef.current = true;
  };

  const handleDiscard = () => {
    if (isVariationMapDirty) {
      discardModal.onOpenModal();

      return;
    }

    shouldRemoveItemRef.current = false;
    navigate(`${PATHS.PRODUCT_EDIT(productCode)}#${variationCode}`);
  };

  const handleDiscardConfirm = () => {
    skipNextBlock();
    shouldRemoveItemRef.current = false;
    discardModal.onCloseModal();
    navigate(`${PATHS.PRODUCT_EDIT(productCode)}#${variationCode}`);
  };

  const handleVariationTemplateNext = async () => {
    try {
      if (
        !selectedTemplate ||
        createTemplateVariations.isLoading ||
        isSubmitting
      ) {
        return;
      }
      setIsSubmitting(true);
      shouldRemoveItemRef.current = false;

      const variations = Object.entries(variationMap)
        .map(([order, variation]) => ({
          order: Number(order),
          product_code: variation.variationCode,
          ...(variation.showFlavors !== undefined && {
            config: { showFlavors: variation.showFlavors },
          }),
        }))
        .sort((a, b) => a.order - b.order);

      const matchedVariation = product?.productVariations?.find(
        (v) => v.product_code === variationCode,
      );

      const payload = {
        template_id: selectedTemplate.id,
        variations,
        narrative: (
          narrativeGenerators[COLLECTIONS.VARIATION_TEMPLATES][
            USER_ACTIONS.UPDATE
          ] as (name: string) => string
        )(matchedVariation?.name || variationCode),
      };

      await createTemplateVariations.mutateAsync(payload);
      shouldRemoveItemRef.current = false;
      Snackbar.success(`${lang.dyDisplay} ${lang.successfullyUpdated}`);
      navigate(`${PATHS.PRODUCT_EDIT(productCode)}#${variationCode}`);
    } catch (error) {
      Snackbar.error(`${lang.dyDisplay} ${lang.failedToSave}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    discardModal,
    handleDiscard,
    handleDiscardConfirm,
    handleVariationTemplateNext,
    initialVariationMap,
    isVariationMapDirty,
  };
};
